/** @jsxImportSource @emotion/react */
import React, { useState,useEffect,useRef } from "react";
import { css, useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { ProjectsModel } from "../../../../services/projectsService";
import AnalysisProjectsSearch from "./AnalysisProjectsSearch";
import AnalysisSummary from "./AnalysisSummary";
import PositiveList from "./PositiveList";
import EditAnalyse from "./EditAnalyse";
import { OnSortProps, THeaderProps } from "../../DataTable/THeader";
import DataTable, {sortingColumnsProps} from "../../DataTable/DataTable";
import { listRulesets } from "../../../../services/rulesetService";
import { useLanguage } from "LanguageContext";
import { REACT_APP_NEW_THEME } from "services/config";
interface AnalysisTableProps {
  analysis: any;
  sortingColumns: sortingColumnsProps;
  setSortingColumns: (sortingColumns: any) => void;
  onDelete: (analysisId: string) => void;
  onEdit: (analysisId: string, updatedAnalysis: any) => void;
  isTableDataLoading: boolean;
  setPageNo: (page: number) => void;
  pageNo:number;
  setRowsPerPage: (rowsPerPage: number) => void;
  rowsPerPage: number;
}
declare global {
  interface Window {
    analysisTable: {
      pageNumber: number;
      pageSize: number;
    };
  }
}

export default function AnalysisTable(props: Readonly<AnalysisTableProps>) {
  const { t,language } = useLanguage();
  const { rowsPerPage, setRowsPerPage } = props;
  const theme = useTheme();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectsModel | null>(null);
  const [openDialogId, setOpenDialogId] = useState<string | null>(null);
  const [rulesetObj, setRulesetObj] = useState<{ [key: string]: string } | null >(null);
  const [rulesetCategory, setRulesetCategory] = useState<{ [key: string]: string } | null >(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterAnalysis, setFilterAnalysis] = useState<any[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean | false>(true);

  const paginatedAnalysisRef = useRef<any[]>([]);
  const isRuleSetLoadRef = useRef<string>("");
  const totalRowsRef = useRef<number>(0);

  useEffect(() => {
    if(isRuleSetLoadRef.current !== language){
      isRuleSetLoadRef.current = language;
      listRulesets(1, 100).then(rulesetList => {
        const _rulesetObj: { [key: string]: string } = {};
        const _rulesetCategory: { [key: string]: string } = {};
        rulesetList?.results.forEach((_ruleset: any) => {
          if (_ruleset.stage && !window.location.hostname.includes(REACT_APP_NEW_THEME)) {
            _rulesetObj[_ruleset?.name] = `${t(_ruleset?.front_name)} (${t(_ruleset.stage)})`;
            _rulesetObj[_ruleset?.id] = `${t(_ruleset?.front_name)} (${t(_ruleset.stage)})`;
            _rulesetCategory[_ruleset?.id] = `${t(_ruleset?.category)}`;
          } else {
            _rulesetObj[_ruleset?.name] = t(_ruleset?.front_name);
            _rulesetObj[_ruleset?.id] = t(_ruleset?.front_name);
            _rulesetCategory[_ruleset?.id] = `${t(_ruleset?.category)}`;
          }  
        });
        setRulesetObj(_rulesetObj);
        setRulesetCategory(_rulesetCategory);

      });
    }
    }, [language]);

  const loadPagination = ()=>{
    let filteredAnalysis = props.analysis;
    if (project?.id) {
      filteredAnalysis = props.analysis.filter(
        (analyse: any) => project === null || analyse.project?.id === project.id
      );
    } 
    totalRowsRef.current = filteredAnalysis.length;
      paginatedAnalysisRef.current = filteredAnalysis.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      );
    setFilterAnalysis(paginatedAnalysisRef.current || [])
    setTimeout(()=>{
      setIsDataLoaded(false)  
    },500)      
  }

  useEffect(()=>{
    if(currentPage &&  rowsPerPage &&  rulesetObj&& props.analysis){
      loadPagination();
    }
  },[currentPage, rowsPerPage, rulesetObj, props.analysis])

  useEffect(()=>{
    loadPagination();      
  },[project]) 

  useEffect(()=>{
    setCurrentPage(1); 
  },[project]) 
  const handlePageChange = (page: number) =>{
    if (page) {
      setCurrentPage(page); 
      props.setPageNo(page);
      if(!window?.analysisTable) {
        window.analysisTable = {
          pageNumber: page,
          pageSize: rowsPerPage
        }
      } else {
        window.analysisTable.pageNumber = page;
      }
    }
  } 
  const inputRowStyles = css`
    padding-top: ${theme.spacing["4xl"]};
    display: flex;
    gap: ${theme.spacing["4xl"]};
    > div {
      min-width: 200px;
      max-width: 40%;
    }
  `;
 
  const handleRowClick = (analyse: any, event: React.MouseEvent) => {
    if (openDialogId !== null) {
      event.stopPropagation();
      return;
    }
    if(analyse?.status==='draft'){
      navigate(`/analyses/${analyse.id}`);
    }else{
      navigate(`/analyses/results?analysis=${analyse.id}`);
    }
    
  };
  const onDialogOpen = (analyseId: string) => setOpenDialogId(analyseId);
  const onDialogClose = () => setOpenDialogId(null);
  const tableColumns : THeaderProps[] = [
    {
      key: "name",
      width: "17%",
      title: t("label_campaign"),
      isSortable: true,
      sortDirection: null,
      onSort: ({key, direction}: OnSortProps) => {
        props.setSortingColumns({
          [key]: direction
        });
       
      },
    },
    {
      key: "notes",
      width: "8%",
      title: t("label_note"),
      isSortable: true,
      sortDirection: null,
      onSort: ({key, direction}: OnSortProps) => {
        props.setSortingColumns({
          [key]: direction
        });
        
      },
    },
    {
      key: "created_at",
      width: "17%",
      title: t("analyse_table_head_date"),
      isSortable: true,
      sortDirection: null,
      onSort: ({key, direction}: OnSortProps) => {
        props.setSortingColumns({
          [key]: direction
        });
       
      },
      renderRowContent: (rowData: any) => <>{new Date(rowData.created_at).toDateString()}</>,
    },
    {
      key: "evaluation",
      width: "25%",
      title: t("analyse_table_head_evaluation"),
      isSortable: false,
      sortDirection: null,
      className: "cai-text-left",
      onSort: ({key, direction}: OnSortProps) => {
        props.setSortingColumns({
          [key]: direction
        });
        
      },
      renderRowContent: (rowData: any) => {
        const firstRuleSet = rowData?.ruleset_id?.split(",")[0];
        const ruleType = rulesetCategory?.[firstRuleSet]?.toLowerCase() ?? "";
        if (rowData.status === "draft") {
          return  <>{t(rowData.status)}</>
        }
        if (rowData.status === "error") {
          return  <>{t("error_title")}</>
        }
        if (ruleType === "compliance" || ruleType === "conformité") {
          return <AnalysisSummary analysis={rowData} />
        }
        return <PositiveList show="smallIcon" analysis={rowData} />
      },      
    },
    {
      key: "ruleset.name",
      width: "25%",
      title: t("analyse_table_head_reference_frame"),
      isSortable: true,
      sortDirection: null,
      onSort: ({key, direction}: OnSortProps) => {
        props.setSortingColumns({
          [key]: direction
        });        
      
      },
      renderRowContent: (rowData: any) => {
        let content = null;
        content = rowData?.ruleset_id?.split(',').map((id: string) => rulesetObj?.[id.trim()]).join(', ');
        if (rowData?.political_risk_ruleset_id) {
          content += `, ${rulesetObj?.[rowData?.political_risk_ruleset_id]}`;
        }
        return (<>{content}</>)
      },
    },
    {
      key: "actions",
      width: "8%",
      isSortable: false,
      title: "",
      className: "",
      sortDirection: null,
      onSort: () => {},
      renderRowContent: (rowData: any) => (
        <EditAnalyse
          analyse={rowData}
          onDelete={props.onDelete}
          onDialogOpen={onDialogOpen}
          onDialogClose={onDialogClose}
          onEdit={props.onEdit} />
      ), 
    }
  ];
  return (
    <>
      <div css={inputRowStyles}>
        <AnalysisProjectsSearch setProject={setProject} showProjectName={true} />
      </div>
      <DataTable
        tableData={filterAnalysis}
        totalRows={totalRowsRef.current}
        handlePageChange={(pageNumber)=> {
          handlePageChange(pageNumber);
        }}
        handleRowsPerPageChange={(rowsPerPage) => {
          setRowsPerPage(rowsPerPage);
          if (!window.analysisTable) {
            window.analysisTable = {
              pageNumber: props.pageNo || 1,
              pageSize: rowsPerPage
            };
          } else {
            window.analysisTable.pageSize = rowsPerPage;
          }
        }}
        columns={tableColumns}
        handleRowClick={(rowData: any, event:React.MouseEvent) => handleRowClick(rowData, event)}
        isTableDataLoading={isDataLoaded || props.isTableDataLoading}
        defaultPage={props.pageNo}
        setPageNo={props.setPageNo}
        defaultRowsPerPage={rowsPerPage}
      />
     
    </>
  );
}
